import moment from 'moment';
import * as Yup from 'yup';
import { FormValues } from '../components/room-posting/drawer form/fo-roomposting-discount-form';
import { isValidMoment } from '@utils/schema';

export interface roomPostingSearchInterface {
  roomNo: number;
  dateFilter: string;
  viewFilter: string;
}

export const entrySchema = Yup.object().shape({
  properties: Yup.array()
    .of(
      Yup.object().shape({
        property: Yup.object().shape({
          propertyID: Yup.string().min(36).max(36).required(),
          entries: Yup.array().of(
            Yup.object().shape({
              entry: Yup.object().shape({
                transaction_identifier: Yup.string().min(36).max(36).required(),
                cutoff_date: Yup.string()
                  .matches(
                    /^\d{4}-\d{2}-\d{2}$/,
                    'Must be in YYYY-MM-DD format',
                  )
                  .required(),
                quantity: Yup.number().min(1).max(100).required(),
                gross_amount: Yup.number().default(0.0).required(),
                mapping_type: Yup.number().oneOf([1, 2, 3, 4]).required(),
                module_id: Yup.number().required(),
                transaction_type: Yup.number().oneOf([1, 2, 3, 4]).required(),
                user_id: Yup.string().min(36).max(36).required(),
                dr_cr_adj: Yup.number().oneOf([1, 2, 3]).required(),
                item: Yup.string().max(120).required(),
                discount: Yup.number().default(0.0).required(),
                discount_type: Yup.number().oneOf([1, 2, 3, 4, 5]).required(),
                sub_group: Yup.number().required(),
                category_type: Yup.number().oneOf([1, 2, 3, 4]).required(),
                line_remarks: Yup.string().optional().nullable(),
                discount_remarks: Yup.string().optional().nullable(),
              }),
            }),
          ),
        }),
      }),
    )
    .min(1)
    .required(),
});

export const RoomPostingEntrySchema = (
  tabSelect: number,
  remarksRequired: boolean,
) => {
  const baseSchema = Yup.object().shape({
    quantity: Yup.number().min(1).max(100).required(),
    gross_amount: Yup.number()
      .notOneOf([0], 'Amount cannot be zero')
      .typeError('Amount is required')
      .required(),
    item: Yup.string().min(3).max(120).required(),
    line_remarks: remarksRequired
      ? Yup.string()
          .min(5, 'Remarks must be at least 5 characters')
          .max(100, 'Remarks cannot exceed 100 characters')
          .typeError('Remarks is required')
          .required()
      : Yup.string().notRequired(),
    payment_details: Yup.object().notRequired(),
    or_no: Yup.string().notRequired(),
    issuing_bank: Yup.string().notRequired(),
    check_date: Yup.object().default(moment()).notRequired(),
    check_no: Yup.string().notRequired(),
    gc_no: Yup.string().notRequired(),
    card_network: Yup.string().notRequired(),
    cc_issuing_bank: Yup.string().notRequired(),
    card_no: Yup.string().notRequired(),
    card_holders_name: Yup.string().notRequired(),
    expiration_date: Yup.string().notRequired(),
    approval_code: Yup.string().notRequired(),
    card_type: Yup.string().notRequired(),
    cl_payment_breakdown: Yup.array().notRequired(),
  });

  const checkSchema = Yup.object().shape({
    issuing_bank: Yup.string().typeError('Issuing Bank is required').required(),
    check_date: Yup.object().default(moment()).label('Check Date').required(),
    check_no: Yup.string().typeError('Issuing Bank is required').required(),
  });

  const giftCertificateSchema = Yup.object().shape({
    gc_no: Yup.string().typeError('GC No. is required').required(),
  });

  const cardDetailsSchema = Yup.object().shape({
    card_network: Yup.string().typeError('Card Network is required').required(),
    cc_issuing_bank: Yup.string()
      .typeError('Issuing Bank is required')
      .required(),
    card_no: Yup.string()
      .length(4, 'Card No. must be exactly 4 characters')
      .typeError('Card No. is required')
      .required(),
    card_holders_name: Yup.string()
      .typeError('Card Holder Name is required')
      .required(),
    expiration_date: Yup.string()
      .typeError('Expiration Date is required')
      .matches(
        /^(0[1-9]|1[0-2])\/\d{2}$/,
        'Expiration Date must be in the format MM/YY',
      )
      .length(5, 'Expiration Date must be exactly 5 characters')
      .required(),
    approval_code: Yup.string()
      .typeError('Approval Code is required')
      .min(6, 'Approval Code must be at least 6 characters')
      .max(8, 'Approval Code must be at most 8 characters')
      .required(),
    card_type: Yup.string().typeError('Card Type is required').required(),
  });

  switch (tabSelect) {
    case 0:
      return baseSchema.concat(checkSchema);
    case 1:
      return baseSchema.concat(giftCertificateSchema);
    case 2:
      return baseSchema.concat(cardDetailsSchema);
    default:
      return baseSchema;
  }
};

export const modifyServiceChargeSchema = Yup.object().shape({
  service_charge: Yup.number()
    .typeError('Service Charge must be a numeric value.')
    .required('Please input Service Charge')
    .min(0, 'Service Charge must be greater than zero')
    .max(15, 'Service Charge must be less than or equal to 15')
    .label('Service Charge'),
});
export const itemEntryRemarksSchema = Yup.object().shape({
  itemRemarks: Yup.string()
    .transform((value) => (value?.trim() === '' ? null : value))
    .nullable()
    .notRequired()
    .max(100, 'Remarks must not exceed 100 characters.')
    .test(
      'min-if-not-empty',
      'Remarks must contain a minimum of 5 characters.',
      (value) => !value || value.trim().length >= 5,
    ),
});
export const voidRemarksSchema = Yup.object().shape({
  itemRemarks: Yup.string()
    .required('Please input Void Remarks')
    .min(5, 'Remarks must contain a minimum of 5 characters.')
    .max(100, 'Remarks must not exceed 100 characters.'),
});
export const creditMemoSchema = Yup.object().shape({
  itemRemarks: Yup.string()
    .required('Please input Credit Memo Remarks')
    .min(5, 'Credit Memo Remarks must contain a minimum of 5 characters.')
    .max(100, 'Credit Memo Remarks must not exceed 100 characters.'),
  credit_amount: Yup.number()
    .typeError('Credit Amount must be a numeric value.')
    .required('No credit amount')
    .min(1, 'Credit Amount must atleast greater than one(1).'),
  guest_name: Yup.string().required('No selected guest'),
});

const idSchema = Yup.object().shape({
  id: Yup.string()
    .required('required')
    .max(16, 'ID must not exceed 16 characters'),
  name: Yup.string().required('required'),
  childName: Yup.string().nullable().optional(),
  birthdate: isValidMoment().nullable().optional(),
  age: Yup.number().nullable().optional(),
  tin: Yup.string().nullable().optional(),
});

const soloParentIDSchema = Yup.object().shape({
  id: Yup.string()
    .required('required')
    .max(16, 'ID must not exceed 16 characters'),
  name: Yup.string().required('required'),
  childName: Yup.string().required('required'),
  birthdate: isValidMoment().required(),
  age: Yup.number().required('required'),

  tin: Yup.string().nullable().optional(),
});

const seniorPWDAthleteIDOptSchema = Yup.object().shape({
  id_list: Yup.array().of(idSchema).nullable().optional(),
});

const seniorPWDAthleteIDSchema = (title: string) =>
  Yup.object().shape({
    id_list: Yup.array()
      .of(title === 'solo_parent_id' ? soloParentIDSchema : idSchema)
      .test('unique-ids', 'Duplicate ID detected', function (value) {
        if (!value) return true;

        const ids = value.map((item) => item.id);
        const duplicates: number[] = [];

        ids.forEach((id, index) => {
          if (!id) return;

          const firstIndex = ids.indexOf(id);
          if (firstIndex !== index) duplicates.push(index);
        });

        if (duplicates.length === 0) return true;

        return this.createError({
          message: 'Duplicate ID',
          path: `${title}.id_list.${duplicates[0]}.id`,
        });
      })
      .test('unique-names', 'Duplicate Name detected', function (value) {
        if (!value) return true;

        const names = value.map((item) => item.name?.trim().toLowerCase());
        const duplicates: number[] = [];

        names.forEach((name, index) => {
          if (!name) return;

          const firstIndex = names.indexOf(name);
          if (firstIndex !== index) duplicates.push(index);
        });

        if (duplicates.length === 0) return true;

        return this.createError({
          message: 'Duplicate Name',
          path: `${title}.id_list.${duplicates[0]}.name`,
        });
      })
      .test(
        'unique-child-names',
        'Duplicate Child Name detected',
        function (value) {
          if (!value || title !== 'solo_parent_id') return true;

          const names = value.map((item) =>
            item.childName?.trim().toLowerCase(),
          );
          const duplicates: number[] = [];

          names.forEach((name, index) => {
            if (!name) return;

            const firstIndex = names.indexOf(name);
            if (firstIndex !== index) duplicates.push(index);
          });

          if (duplicates.length === 0) return true;

          return this.createError({
            message: 'Duplicate Name of Child',
            path: `${title}.id_list.${duplicates[0]}.childName`,
          });
        },
      ),
  });

export const applyDiscountSchema = (
  discountType: string,
  isPercentageUse: boolean,
  defaultSpecialDiscType: 'Senior' | 'PWD' | 'Athlete' | 'Solo Parent',
  totalSenior: number,
  totalPWD: number,
  totalAthlete: number,
  totalSoloParent: number,
  isCustomList?: boolean,
) => {
  const specialDiscountsSchema = Yup.object().shape({
    manual_discount: Yup.number().notRequired(),
    percentage_discount: Yup.number().notRequired(),

    senior_pax:
      (totalPWD < 1 ||
        totalAthlete < 1 ||
        totalSenior < 1 ||
        totalSoloParent < 1) &&
      defaultSpecialDiscType === 'Senior'
        ? totalSenior < 0 ||
          totalSenior > 100 ||
          (totalPWD === 0 &&
            totalAthlete === 0 &&
            totalSenior === 0 &&
            totalSoloParent === 0)
          ? Yup.number()
              .typeError('Total Pax must be a numeric value.')
              .required('Total Pax is required and must be greater than zero')
              .min(1, 'Total Pax must be greater than zero')
              .max(100, 'Total Pax must be less than or equal to 100')
              .label('Total Pax')
          : Yup.number()
              .notRequired()
              .typeError('Total Pax must be a numeric value.')
        : Yup.number()
            .notRequired()
            .typeError('Total Pax must be a numeric value.'),

    pwd_pax:
      (totalPWD < 1 ||
        totalAthlete < 1 ||
        totalSenior < 1 ||
        totalSoloParent < 1) &&
      defaultSpecialDiscType === 'PWD'
        ? totalPWD < 0 ||
          totalPWD > 100 ||
          (totalPWD === 0 &&
            totalAthlete === 0 &&
            totalSenior === 0 &&
            totalSoloParent === 0)
          ? Yup.number()
              .typeError('Total Pax must be a numeric value.')
              .required('Total PWD Pax is required')
              .min(1, 'Total PWD Pax must be greater than zero')
              .max(100, 'Total PWD Pax must be less than or equal to 100')
              .label('Total Pax')
          : Yup.number()
              .notRequired()
              .typeError('Total Pax must be a numeric value.')
        : Yup.number()
            .notRequired()
            .typeError('Total Pax must be a numeric value.'),

    athlete_pax:
      (totalPWD < 1 ||
        totalAthlete < 1 ||
        totalSenior < 1 ||
        totalSoloParent < 1) &&
      defaultSpecialDiscType === 'Athlete'
        ? totalAthlete < 0 ||
          totalAthlete > 100 ||
          (totalPWD === 0 &&
            totalAthlete === 0 &&
            totalSenior === 0 &&
            totalSoloParent === 0)
          ? Yup.number()
              .typeError('Total Pax must be a numeric value.')
              .required('Total Athlete Pax is required')
              .min(1, 'Total Athlete Pax must be greater than zero')
              .max(100, 'Total Athlete Pax must be less than or equal to 100')
              .label('Total Athlete Pax')
          : Yup.number()
              .notRequired()
              .typeError('Total Pax must be a numeric value.')
        : Yup.number()
            .notRequired()
            .typeError('Total Pax must be a numeric value.'),

    solo_parent_pax:
      (totalPWD < 1 ||
        totalAthlete < 1 ||
        totalSenior < 1 ||
        totalSoloParent < 1) &&
      defaultSpecialDiscType === 'Solo Parent'
        ? totalSoloParent < 0 ||
          totalSoloParent > 100 ||
          (totalPWD === 0 &&
            totalAthlete === 0 &&
            totalSenior === 0 &&
            totalSoloParent === 0)
          ? Yup.number()
              .typeError('Total Solo Parent Pax must be a numeric value.')
              .required(
                'Total Solo Parent Pax is required and must be greater than zero',
              )
              .min(1, 'Total Solo Parent Pax must be greater than zero')
              .max(
                100,
                'Total Solo Parent Pax must be less than or equal to 100',
              )
              .label('Total Solo Parent Pax')
          : Yup.number()
              .notRequired()
              .typeError('Total Solo Parent Pax must be a numeric value.')
        : Yup.number()
            .notRequired()
            .typeError('Total Solo Parent Pax must be a numeric value.'),

    senior_id:
      totalSenior > 0
        ? seniorPWDAthleteIDSchema('senior_id').nullable().optional()
        : seniorPWDAthleteIDOptSchema.notRequired(),
    pwd_id:
      totalPWD > 0
        ? seniorPWDAthleteIDSchema('pwd_id').nullable().optional()
        : seniorPWDAthleteIDOptSchema.notRequired(),
    athlete_id:
      totalAthlete > 0
        ? seniorPWDAthleteIDSchema('athlete_id').nullable().optional()
        : seniorPWDAthleteIDOptSchema.notRequired(),
    solo_parent_id:
      totalSoloParent > 0
        ? seniorPWDAthleteIDSchema('solo_parent_id').nullable().optional()
        : seniorPWDAthleteIDOptSchema.notRequired(),
  });

  const regularDiscountsSchema = Yup.object().shape({
    manual_discount: isPercentageUse
      ? Yup.number()
          .notRequired()
          .typeError('Manual discount must be a numeric value.')
      : Yup.number()
          .typeError('Manual discount must be a numeric value.')
          .required('Manual discount is required')
          .min(1, 'Manual discount must be greater than zero')
          .label('Manual Discount'),
    percentage_discount: isCustomList
      ? Yup.number()
          .typeError('Percentage discount must be a numeric value.')
          .label('Percentage Discount')
      : isPercentageUse
        ? Yup.number()
            .typeError('Percentage discount must be a numeric value.')
            .required('Percentage discount is required')
            .min(1, 'Percentage discount must be greater than zero')
            .max(100, 'Percentage discount must be less than or equal to 100')
            .label('Percentage Discount')
        : Yup.number()
            .notRequired()
            .typeError('Percentage discount must be a numeric value.'),
    pwd_pax: Yup.number().notRequired(),
    athlete_pax: Yup.number().notRequired(),
    senior_pax: Yup.number().notRequired(),
    senior_id: seniorPWDAthleteIDOptSchema.notRequired(),
    pwd_id: seniorPWDAthleteIDOptSchema.notRequired(),
    athlete_id: seniorPWDAthleteIDOptSchema.notRequired(),
  });

  return discountType === 'Special'
    ? (specialDiscountsSchema as Yup.ObjectSchema<FormValues>)
    : (regularDiscountsSchema as Yup.ObjectSchema<FormValues>);
};

export const taxCreditSchema = Yup.object().shape({
  payor_name: Yup.string()
    .required('Please select Payors Account')
    .label('No selected Payor`s Name.'),
  payor_tin: Yup.string()
    .required('Please select Payors Account with TIN Number')
    .label('No selected Payor`s TIN Number.'),
  tax_code: Yup.string()
    .required('Please select Tax Code')
    .label('No selected Tax Code.'),
  credit_amount: Yup.number()
    .typeError('Credit Amount must be a numeric value.')
    .required('Please input Credit Amount')
    .min(0.01, 'Credit Amount must be greater than zero')
    .label('Credit Amount'),
  net_amount: Yup.number()
    .typeError('Net Amount must be a numeric value.')
    .required('Please input Net Amount')
    .min(0.01, 'Net Amount must be greater than zero')
    .label('Net Amount'),
});

export const creditMemoUsedSchema = Yup.object().shape({
  cm_slip_no: Yup.string()
    .required('Please select Credit Memo transaction')
    .label('No selected  Credit Memo transaction.'),
  total_credit: Yup.number()
    .typeError('Total Credit Amount must be a numeric value.')
    .required('Please input Total Credit Amount')
    .min(0.01, 'Total Credit Amount must be greater than zero')
    .label('Total Credit Amount'),
  credit_avail: Yup.number()
    .typeError('Credit Available Amount must be a numeric value.')
    .required('Please input Credit Available Amount')
    .min(0.01, 'Credit Available Amount must be greater than zero')
    .label('Credit Available Amount'),
  amount_used: Yup.number()
    .typeError('Amount apply must be a numeric value.')
    .required('Please input Amount to apply')
    .min(0.01, 'Amount must be greater than zero')
    .label('Apply Amount'),
  cmRemarks: Yup.string()
    .required('Please enter credit memo remarks')
    .label('No entered credit memo remarks.')
    .min(5, 'Credit Memo Remarks must contain a minimum of 5 characters.')
    .max(100, 'Credit Memo Remarks must not exceed 100 characters.'),
});
